import * as React from 'react';
import { FC, useMemo } from 'react';

import { Box } from '@rbilabs/universal-components';

import { IDiscount, IFee } from '@rbi-ctg/menu';
import Currency from 'components/currency';
import {
  getFeesByGroup,
  getStandardizedName,
} from 'experiments/checkout-v2/control/pages/cart/your-cart/totals/utils';
import { t } from 'state/intl/translations';
import { TranslationKey } from 'state/intl/types';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useUIContext } from 'state/ui';
import { primitive } from 'styles/constants';
import { computeOtherDiscountAmount } from 'utils/cart/helper';
import { getDeliveryFees } from 'utils/order';

import { geographicalFeeLabelIdMap } from '../constants';
import { MinimalAccountOrder } from '../types';

import getRewardsDiscount from './get-rewards-discount';
import {
  FeeDeliveryItem,
  ReceiptDetail,
  ReceiptDetailsText,
  RewardsDiscountDetail,
  SDiscount,
} from './receipt-details.styled';

const mapMinimalDiscountsToDiscounts = (
  discounts?: ReadonlyArray<{ name?: string | null; value?: number | null } | null> | null
): IDiscount[] =>
  (discounts ?? []).flatMap(discount =>
    discount?.name != null && discount?.value != null
      ? [
          {
            name: discount.name as IDiscount['name'],
            value: discount.value,
          },
        ]
      : []
  );

const mapMinimalFeesToFees = (
  fees?: ReadonlyArray<{
    name?: string | null;
    priceCents?: number | null;
    quantity?: number | null;
    totalCents?: number | null;
  } | null> | null
): IFee[] =>
  (fees ?? []).flatMap(fee =>
    fee?.name != null && fee?.priceCents != null && fee?.quantity != null && fee?.totalCents != null
      ? [
          {
            name: fee.name,
            priceCents: fee.priceCents,
            quantity: fee.quantity,
            totalCents: fee.totalCents,
          },
        ]
      : []
  );

const MinimalReceiptDetails: FC<React.PropsWithChildren<{ order: MinimalAccountOrder }>> = ({
  order,
}) => {
  const { formatCurrencyForLocale: currencyFormatter } = useUIContext();
  const { delivery } = order;
  const { subTotalCents, taxCents, totalCents } = order.cart;

  const {
    feeCents: deliveryFeeCents,
    isFreeDelivery,
    deliveryFeeDiscountCents,
    geographicalFeeCents,
    serviceFeeCents,
    smallCartFeeCents,
  } = getDeliveryFees({
    delivery: delivery
      ? {
          dropoff: {
            state: delivery.dropoff?.state ?? '',
          },
          feeCents: delivery.feeCents,
          tipCents: delivery.tipCents ?? 0,
          feeDiscountCents: delivery.feeDiscountCents ?? 0,
          serviceFeeCents: delivery.serviceFeeCents ?? 0,
          smallCartFeeCents: delivery.smallCartFeeCents ?? 0,
          geographicalFeeCents: delivery.geographicalFeeCents ?? 0,
          status: delivery.status,
        }
      : undefined,
  });

  const enableFeesOnOrderReceipt = useFlag(LaunchDarklyFlag.ENABLE_FEES_ON_ORDER_RECEIPT);
  const enableDiscountsOnOrderReceipt = useFlag(LaunchDarklyFlag.ENABLE_DISCOUNTS_ON_ORDER_RECEIPT);

  const discounts = useMemo(() => mapMinimalDiscountsToDiscounts(order.cart.discounts), [
    order.cart.discounts,
  ]);
  const dropoffState = delivery?.dropoff?.state;

  const geographicalFeeLabelId =
    dropoffState && geographicalFeeLabelIdMap.hasOwnProperty(dropoffState)
      ? geographicalFeeLabelIdMap[dropoffState]
      : 'geographicalFee';

  const rewardsDiscountCents = useMemo(() => getRewardsDiscount({ discounts }), [discounts]);

  const otherDiscountAmount = computeOtherDiscountAmount(discounts);
  const fees = useMemo(() => mapMinimalFeesToFees(order.cart.fees), [order.cart.fees]);
  const itemFees = getFeesByGroup(fees);

  return (
    <Box>
      {enableFeesOnOrderReceipt &&
        !!itemFees &&
        Object.entries(itemFees).map(([name, entryCents]: [string, number], index) => {
          const standardizedName = getStandardizedName({ name });
          const feeTranslationKey = `${standardizedName}Fee` as TranslationKey;
          return (
            <ReceiptDetail key={index}>
              <ReceiptDetailsText>{t(feeTranslationKey)}</ReceiptDetailsText>
              <ReceiptDetailsText>{currencyFormatter(entryCents)}</ReceiptDetailsText>
            </ReceiptDetail>
          );
        })}
      {order.delivery && (
        <Box>
          <ReceiptDetail>
            <ReceiptDetailsText>{t('deliveryFee')}:</ReceiptDetailsText>
            {isFreeDelivery ? (
              <FeeDeliveryItem>
                {t('free')}
                &nbsp;
                <SDiscount>
                  <Currency amount={deliveryFeeDiscountCents} />
                </SDiscount>
              </FeeDeliveryItem>
            ) : (
              <ReceiptDetailsText>{currencyFormatter(deliveryFeeCents)}</ReceiptDetailsText>
            )}
          </ReceiptDetail>
          {geographicalFeeCents > 0 && (
            <ReceiptDetail>
              <ReceiptDetailsText>{t(geographicalFeeLabelId)}:</ReceiptDetailsText>
              <ReceiptDetailsText>{currencyFormatter(geographicalFeeCents)}</ReceiptDetailsText>
            </ReceiptDetail>
          )}
          {serviceFeeCents > 0 && (
            <ReceiptDetail>
              <ReceiptDetailsText>{t('serviceFee')}:</ReceiptDetailsText>
              <ReceiptDetailsText>{currencyFormatter(serviceFeeCents)}</ReceiptDetailsText>
            </ReceiptDetail>
          )}
          {smallCartFeeCents > 0 && (
            <ReceiptDetail>
              <ReceiptDetailsText>{t('smallCartFee')}:</ReceiptDetailsText>
              <ReceiptDetailsText>{currencyFormatter(smallCartFeeCents)}</ReceiptDetailsText>
            </ReceiptDetail>
          )}
          <ReceiptDetail>
            <ReceiptDetailsText>{t('tip')}:</ReceiptDetailsText>
            <ReceiptDetailsText>
              {currencyFormatter(order.delivery.tipCents ?? 0)}
            </ReceiptDetailsText>
          </ReceiptDetail>
        </Box>
      )}
      {enableDiscountsOnOrderReceipt && !!otherDiscountAmount && (
        <RewardsDiscountDetail>
          <ReceiptDetailsText>{t('otherDiscounts')}</ReceiptDetailsText>
          <ReceiptDetailsText>{` - ${currencyFormatter(otherDiscountAmount)}`}</ReceiptDetailsText>
        </RewardsDiscountDetail>
      )}
      {!!rewardsDiscountCents && (
        <RewardsDiscountDetail>
          <ReceiptDetailsText color={primitive.$crunchyGreen}>
            {t('rewardSavings')}
          </ReceiptDetailsText>
          <ReceiptDetailsText color={primitive.$crunchyGreen}>
            {` - ${currencyFormatter(rewardsDiscountCents)}`}
          </ReceiptDetailsText>
        </RewardsDiscountDetail>
      )}
      <ReceiptDetail>
        <ReceiptDetailsText>{t('subtotal')}</ReceiptDetailsText>
        <ReceiptDetailsText>{currencyFormatter(subTotalCents ?? 0)}</ReceiptDetailsText>
      </ReceiptDetail>
      <ReceiptDetail>
        <ReceiptDetailsText>{t('tax')}</ReceiptDetailsText>
        <ReceiptDetailsText>{currencyFormatter(taxCents ?? 0)}</ReceiptDetailsText>
      </ReceiptDetail>
      <ReceiptDetail $total>
        <ReceiptDetailsText $total>{t('total')}</ReceiptDetailsText>
        <ReceiptDetailsText $total>{currencyFormatter(totalCents ?? 0)}</ReceiptDetailsText>
      </ReceiptDetail>
    </Box>
  );
};

export default MinimalReceiptDetails;
